/**
 * React bindings of the import UI (W2-09 publish-import): translation through `services.i18n`, the import queue's
 * snapshot, and the open project's store.
 */

import { useCallback, useMemo, useSyncExternalStore } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { Locale, MessageRef, Unsubscribe } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import { importQueue, type ImportQueue, type QueueSnapshot } from './importQueue';
export { useServicesStore as useStore } from '../../app/registry/react';

export interface Text {
  readonly locale: Locale;
  readonly t: (key: MessageKey | string, params?: Readonly<Record<string, string | number>>) => string;
  readonly tr: (ref: MessageRef) => string;
}

export function useText(services: Pick<AppServices, 'i18n'>): Text {
  const { i18n } = services;
  const subscribe = useCallback((cb: () => void): Unsubscribe => i18n.subscribe(cb), [i18n]);
  const get = useCallback((): Locale => i18n.locale, [i18n]);
  const locale = useSyncExternalStore(subscribe, get, get);
  return useMemo(
    () => ({
      locale,
      t: (key, params) => i18n.t(key, params),
      tr: (ref) => i18n.tr(ref),
    }),
    [i18n, locale],
  );
}

/** The import queue's state, live. */
export function useQueue(queue: ImportQueue = importQueue): QueueSnapshot {
  const subscribe = useCallback((cb: () => void): Unsubscribe => queue.subscribe(cb), [queue]);
  const get = useCallback((): QueueSnapshot => queue.snapshot(), [queue]);
  return useSyncExternalStore(subscribe, get, get);
}
